import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessage, ChatConnector, ChatUsageInfo, ChatConfirmationPending, ChatContextUsage } from '../../features/chat/types/chatTypes';
import { ContentBlock } from '../../features/chat/types/contentBlocks';
import {
  addAccountThunk,
  addAccountWithUserData,
  completeAccountSwitchWithUserData,
  processAddAccountThunk,
  switchAccountThunk,
} from '../thunks/multiAccountThunks';

interface ChatState {
  messages: ChatMessage[];
  connectors: ChatConnector[];
  activeConnectorId: number | null;
  isLoading: boolean;
  isPanelOpen: boolean;
  pendingConfirmation: ChatConfirmationPending | null;
  // SSE / per-conversation correlation string. Was historically named `sessionId`;
  // renamed to `correlationId` in Spec B (Requirement 16). Auto-generated client-side
  // and resent as the chat panel stays open. Distinct from the persistent
  // `sessionId` (long) which Spec B Phase 7b will add as a separate field.
  correlationId: string;
  // Persistent chat session id from Spec B. Null on a fresh conversation;
  // populated when the backend's SSE `meta` event returns a created/loaded id.
  sessionId: number | null;
  lastUsage: ChatUsageInfo | null;
  // Spec E (chat-context-compaction) — snapshot of the composed prompt's
  // size relative to the connector's context window, populated from the
  // terminal SSE `meta` event so ContextUsageIndicator can render.
  contextUsage: ChatContextUsage | null;
  connectorsLoaded: boolean;
}

const initialState: ChatState = {
  messages: [],
  connectors: [],
  activeConnectorId: null,
  isLoading: false,
  isPanelOpen: false,
  pendingConfirmation: null,
  correlationId: typeof crypto !== 'undefined' ? crypto.randomUUID() : '',
  sessionId: null,
  lastUsage: null,
  contextUsage: null,
  connectorsLoaded: false,
};

/**
 * Full chat reset for an account-identity change. Wipes messages, pending
 * confirmation, usage, connectors, and regenerates the per-conversation
 * `correlationId`. Shared by the `resetForAccountChange` action (dispatched
 * from AuthProvider's activeAccountId watcher) and the legacy thunk matcher
 * defensively kept in `extraReducers`.
 */
function applyAccountReset(state: ChatState) {
  state.messages = [];
  state.pendingConfirmation = null;
  state.lastUsage = null;
  state.contextUsage = null;
  state.correlationId = typeof crypto !== 'undefined' ? crypto.randomUUID() : '';
  state.sessionId = null;
  state.connectors = [];
  state.activeConnectorId = null;
  state.connectorsLoaded = false;
  state.isLoading = false;
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    togglePanel(state) {
      state.isPanelOpen = !state.isPanelOpen;
    },
    openPanel(state) {
      state.isPanelOpen = true;
    },
    closePanel(state) {
      state.isPanelOpen = false;
    },
    setConnectors(state, action: PayloadAction<ChatConnector[]>) {
      state.connectors = action.payload;
      state.connectorsLoaded = true;
      // Auto-select first connector if current selection is null or
      // no longer exists in the new list (e.g. after account switch).
      const previousActiveId = state.activeConnectorId;
      const stillValid = action.payload.some(c => c.connectorId === state.activeConnectorId);
      if ((!state.activeConnectorId || !stillValid) && action.payload.length > 0) {
        state.activeConnectorId = action.payload[0].connectorId;
      } else if (action.payload.length === 0) {
        state.activeConnectorId = null;
      }
      // Spec E — if the active connector actually changed (auto-promotion
      // to a different model because the previous one vanished), the
      // previous model's context window no longer applies. Clear the
      // indicator so it doesn't lie until the next SSE `meta` event.
      if (state.activeConnectorId !== previousActiveId) {
        state.contextUsage = null;
      }
    },
    setActiveConnector(state, action: PayloadAction<number>) {
      state.activeConnectorId = action.payload;
      // Spec E — the previous connector's context usage is for a different
      // model with a different window. Clear the indicator so it hides
      // until the next SSE `meta` event repopulates it for the new model.
      state.contextUsage = null;
    },
    addUserMessage(state, action: PayloadAction<string>) {
      state.messages.push({
        id: crypto.randomUUID(),
        role: 'user',
        blocks: [{ type: 'text', content: action.payload }],
        timestamp: Date.now(),
      });
    },
    addAssistantMessage(
      state,
      action: PayloadAction<{ blocks: ContentBlock[]; correlationId?: string | null; usage?: ChatUsageInfo | null } | ContentBlock[]>
    ) {
      const payload = action.payload;
      const isArray = Array.isArray(payload);
      const blocks = isArray ? payload : payload.blocks;
      const correlationId = isArray ? undefined : payload.correlationId ?? undefined;
      const usage = isArray ? undefined : (payload as { usage?: ChatUsageInfo | null }).usage ?? undefined;
      state.messages.push({
        id: crypto.randomUUID(),
        role: 'assistant',
        blocks,
        timestamp: Date.now(),
        correlationId: correlationId ?? undefined,
        feedback: null,
        usage: usage ?? undefined,
      });
    },
    // Chat Quality Foundation — optimistic local update of feedback on an
    // assistant message. The actual persistence happens via
    // chatFeedbackService; this reducer just paints the UI.
    setMessageFeedback(
      state,
      action: PayloadAction<{ messageId: string; feedback: 'ThumbsUp' | 'ThumbsDown' | null }>
    ) {
      const { messageId, feedback } = action.payload;
      const target = state.messages.find(m => m.id === messageId);
      if (target) target.feedback = feedback;
    },
    updateLastAssistantMessage(state, action: PayloadAction<ContentBlock[]>) {
      const lastAssistant = [...state.messages].reverse().find(m => m.role === 'assistant');
      if (lastAssistant) {
        lastAssistant.blocks = action.payload;
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setPendingConfirmation(state, action: PayloadAction<ChatConfirmationPending | null>) {
      state.pendingConfirmation = action.payload;
    },
    setUsage(state, action: PayloadAction<ChatUsageInfo | null>) {
      state.lastUsage = action.payload;
    },
    // Spec E — updated once per turn from the SSE `meta` event.
    setContextUsage(state, action: PayloadAction<ChatContextUsage | null>) {
      state.contextUsage = action.payload;
    },
    setSessionId(state, action: PayloadAction<number | null>) {
      state.sessionId = action.payload;
    },
    clearConversation(state) {
      state.messages = [];
      state.pendingConfirmation = null;
      state.lastUsage = null;
      state.contextUsage = null;
      state.correlationId = typeof crypto !== 'undefined' ? crypto.randomUUID() : '';
      state.sessionId = null;
    },
    /**
     * Full reset for an account-identity change. Dispatched from
     * <c>AuthProvider</c>'s effect that watches <c>state.auth.activeAccountId</c>.
     * Catches every account-switch path — sync actions, async thunks, and
     * future code — by reacting to the post-condition (active id changed)
     * rather than the action that caused it.
     */
    resetForAccountChange(state) {
      applyAccountReset(state);
    },
    /**
     * Reset on tenant switch within the same account. Mirrors
     * `resetForAccountChange` because the in-memory conversation may belong
     * to the previous tenant's session, which would fail server-side
     * ownership checks on the next message. Connectors are also wiped so
     * the next load grabs the new tenant's assignments.
     */
    resetForTenantChange(state) {
      applyAccountReset(state);
    },
    loadMessagesFromStorage(state, action: PayloadAction<ChatMessage[]>) {
      state.messages = action.payload;
    },
    /**
     * Bulk-replace the in-memory conversation. Used when the user opens a
     * persisted session from the drawer — `chatSessionThunks.loadSessionDetail`
     * dispatches this with the deserialized messages from the backend so the
     * panel rehydrates without going through `addUserMessage` / `addAssistantMessage`
     * one at a time. Spec B (chat-session-history), Phase 10.
     */
    setMessages(state, action: PayloadAction<ChatMessage[]>) {
      state.messages = action.payload;
      state.pendingConfirmation = null;
      state.lastUsage = null;
      state.contextUsage = null;
    },
  },
  extraReducers: (builder) => {
    // Reset chat state whenever the active user identity changes — either by
    // explicit switch (`switchAccountThunk`) or by adding a new account
    // (`addAccountThunk`, which silently activates the new account in storage
    // without firing `switchAccountThunk`). Without listening to both, the
    // chat panel keeps the previous user's messages and reuses the same
    // `correlationId` across users — a privacy + provenance bug.
    //
    // Connectors are also cleared because different users may have different
    // connector assignments.
    // Defensive matcher: if any of these account thunks DO fire, also reset.
    // The primary mechanism is the AuthProvider effect that watches
    // `state.auth.activeAccountId`, which catches every account-change path
    // including sync actions and future code. This matcher is a backup for
    // the thunks we know about today.
    builder.addMatcher(
      isAnyOf(
        switchAccountThunk.fulfilled,
        addAccountThunk.fulfilled,
        addAccountWithUserData.fulfilled,
        processAddAccountThunk.fulfilled,
        completeAccountSwitchWithUserData.fulfilled,
      ),
      (state) => {
        applyAccountReset(state);
      }
    );
  },
});

export const {
  togglePanel,
  openPanel,
  closePanel,
  setConnectors,
  setActiveConnector,
  addUserMessage,
  addAssistantMessage,
  updateLastAssistantMessage,
  setMessageFeedback,
  setLoading,
  setPendingConfirmation,
  setUsage,
  setContextUsage,
  setSessionId,
  clearConversation,
  resetForAccountChange,
  resetForTenantChange,
  loadMessagesFromStorage,
  setMessages,
} = chatSlice.actions;

export default chatSlice.reducer;
